import React, { type FC } from 'react';
import classnames from 'classnames';
import { useAnalytics, useAuthManager } from '@funtech-int/yango-shared';
import { useInView } from 'react-intersection-observer';
import { RouterLink, useI18n as useI18nManager } from '@funtech-int/rival-react';

import { Button } from '@/shared/components/Button/Button';
import { mainRoute } from '@/scenarios/home';
import { getGiftUrl } from '@/app/lib/getGiftUrl';
import { useI18n } from '@/app/hooks/useI18n';
import { useHeaderVisibility } from '@/app/hooks/useHeaderVisibility';
import { createAnalyticsEvents } from '@/app/lib/createAnalyticsEvents';
import { ProfileContainer } from '@/shared/components/Profile/Profile';
import { PageId } from '@/shared/types/pathId';
import { HeaderType } from '@/app/types/layout';
import { clientConfig } from '@/config/client';
import { HeaderLogo } from '@/shared/assets/HeaderLogo';

import { LanguageSwitcher } from '../LanguageSwitcher/LanguageSwitcher';
import { HeaderSubscriptionButton } from '../HeaderSubscriptionButton/HeaderSubscriptionButton';

import styles from './Header.module.css';
import { Navigation } from './_Navigation';

const { brandName } = clientConfig;

interface HeaderProps {
    type: HeaderType;
    shouldShowSubscribeButton?: boolean;
}

interface HeaderContent {
    isHeaderVisible: boolean;
    type: HeaderType;
    isShowButtonPlus: boolean;
}

const HeaderContent = ({ isHeaderVisible, type, isShowButtonPlus }: HeaderContent) => {
    const i18n = useI18n();
    const { lang } = useI18nManager().locale.get();
    const { isAuthorized } = useAuthManager();
    const analytics = useAnalytics(createAnalyticsEvents);

    if (type === 'only-logo') {
        return null;
    }

    return (
        <>
            <Navigation isHeaderVisible={isHeaderVisible} />
            <div className={styles.avatarWrapper}>
                {!isAuthorized && (
                    <>
                        <Button
                            as="a"
                            href={getGiftUrl(lang)}
                            rel="noreferrer"
                            target="_blank"
                            variant="secondary"
                            size={32}
                            theme="dark"
                            className={styles.promocodeButton}
                        >
                            {i18n('promocode__button_title')}
                        </Button>
                        <LanguageSwitcher />
                    </>
                )}
                {isShowButtonPlus && isAuthorized && (
                    <HeaderSubscriptionButton className={styles.buttonPlusLink} />
                )}
                <div className={styles.profileMenuWrapper}>
                    <ProfileContainer
                        dropDownMenuClassName={styles.dropDownMenu}
                        onLoginClick={() => analytics.headerNavigated(PageId.LOGIN, false)}
                    />
                </div>
            </div>
        </>
    );
};

const HeaderLogoLink = () => (
    <div className={styles.logoContainer}>
        <RouterLink to={mainRoute()} className={styles.headerLogoLink} aria-label={brandName}>
            <HeaderLogo className={styles.headerLogo} />
        </RouterLink>
    </div>
);

export const Header: FC<HeaderProps> = ({ type, shouldShowSubscribeButton = true }) => {
    const analytics = useAnalytics(createAnalyticsEvents);

    const { ref: visibilityRef } = useInView({
        triggerOnce: true,
        threshold: 1,
        onChange(inView) {
            if (inView) {
                analytics.headerShowed();
            }
        },
    });

    const { isHeaderVisible, isGradientVisible } = useHeaderVisibility();

    const classList = classnames(styles.root, {
        [styles.root_visible]: isHeaderVisible,
        [styles[`root_${type}`]]: type,
    });

    return (
        <div
            className={classnames(styles.wrapper, {
                [styles.overlay]: type !== 'base',
            })}
            ref={visibilityRef}
        >
            <header className={classList} data-tid="Header">
                <div
                    className={classnames(styles.gradient, {
                        [styles.visible]: isGradientVisible,
                    })}
                />
                <div className={styles.content}>
                    <HeaderLogoLink />
                    <HeaderContent
                        isHeaderVisible={isHeaderVisible}
                        type={type}
                        isShowButtonPlus={shouldShowSubscribeButton}
                    />
                </div>
            </header>
        </div>
    );
};
